import { Link } from "@remix-run/react";
import { ArrowUpRight, CloudUpload } from "lucide-react";
import { trpc } from "../../../../lib/trpc";
import { getLandingCopy } from "../../../data/landingCopy";
import { getUploadExperienceCopy } from "../../../data/uploadExperienceCopy";
import { useTranslation } from "../../../i18n";
import { LanguageSwitcher } from "../../LanguageSwitcher";
import "./share-landing.css";

interface ShareLandingPageProps {
	onChooseFile: () => void;
	onFileSelect: (file: File) => void;
	selectionError?: string | null;
}

export function ShareLandingPage({ onChooseFile, selectionError }: ShareLandingPageProps) {
	const { t, locale, localizedPath } = useTranslation();
	const { data: user } = trpc.auth.me.useQuery(undefined, { retry: false, networkMode: "always" });
	const copy = getLandingCopy(locale);
	const uploadCopy = getUploadExperienceCopy(locale);

	return (
		<div className="share-landing">
			<section className="share-landing-hero" aria-labelledby="share-product-name">
				<header className="share-landing-header">
					<Link to={localizedPath("/")} className="share-landing-brand" aria-label="GSplat Share">
						<img className="share-brand-mark" src="/images/gsplat-share-mark.png" alt="" />
						<span>GSplat Share</span>
					</Link>
					<nav aria-label={t("nav.main")}>
						<a href="#how-it-works">{t("nav.howItWorks")}</a>
						<a href="#formats">{t("nav.formats")}</a>
						<Link to={localizedPath("/mypage")}>{t("nav.dashboard")}</Link>
						{!user && (
							<a href="/share/auth/google" className="share-header-login">
								{t("nav.login")}
							</a>
						)}
						<LanguageSwitcher className="share-language" />
					</nav>
				</header>

				<div className="share-hero-intro">
					<div className="share-hero-message">
						<p className="share-hero-kicker">{t("upload.studioLabel")}</p>
						<h1 id="share-product-name">{t("upload.introTitle")}</h1>
						<p className="share-hero-description">{t("upload.introDescription")}</p>
						{selectionError && (
							<p role="alert" className="share-selection-error">
								{selectionError}
							</p>
						)}
						<div className="share-hero-secondary">
							<Link to={localizedPath("/mypage")}>
								{copy.manage} <span aria-hidden="true">↗</span>
							</Link>
							<Link to={localizedPath("/import/drive")}>
								{locale === "ja" ? "Google Driveのリンクから取り込む →" : "Import a Google Drive link →"}
							</Link>
						</div>
					</div>
					<section className="share-upload-launch" aria-label={t("upload.workspaceTitle")}>
						<div className="share-upload-symbol" aria-hidden="true">
							<CloudUpload size={44} strokeWidth={1.5} />
						</div>
						<p className="share-upload-eyebrow">UPLOAD YOUR 3D</p>
						<h2>{uploadCopy.dropTitle}</h2>
						<p className="share-upload-hint">{uploadCopy.dropHint}</p>
						<div className="share-hero-actions">
							<button type="button" onClick={onChooseFile} className="share-action share-action-primary">
								{uploadCopy.choose}
								<ArrowUpRight size={22} aria-hidden="true" />
							</button>
						</div>
						<small>{uploadCopy.formats}</small>
					</section>
				</div>

				<div className="share-hero-gallery" aria-label={copy.heroAlt}>
					<figure className="share-hero-gallery-main">
						<img src="/images/gsplat-share-hero.webp" alt={copy.heroAlt} fetchPriority="high" />
					</figure>
					<figure className="share-hero-gallery-detail">
						<img src="/images/gsplat-share-place.webp" alt={copy.recipientAlt} />
					</figure>
					<div className="share-hero-gallery-caption" aria-hidden="true">
						<span>{copy.heroCaption}</span>
						<span>PLY / SPZ / SOG / HTML</span>
					</div>
				</div>
			</section>

			<main>
				<section className="share-use-cases" aria-labelledby="share-use-cases-title">
					<div className="share-section-heading">
						<span className="share-section-index">02</span>
						<h2 id="share-use-cases-title">{copy.useCasesTitle}</h2>
						<p>{copy.useCasesLead}</p>
					</div>
					<ol>
						{copy.useCases.map((item, index) => (
							<li key={item.title}>
								<span>0{index + 1}</span>
								<h3>{item.title}</h3>
								<p>{item.body}</p>
							</li>
						))}
					</ol>
				</section>

				<section className="share-feature share-feature-place" aria-labelledby="share-recipient-title">
					<figure>
						<img src="/images/gsplat-share-place.webp" alt={copy.recipientAlt} />
					</figure>
					<div className="share-feature-copy">
						<span className="share-section-index">03</span>
						<h2 id="share-recipient-title">{copy.recipientTitle}</h2>
						<p>{copy.recipientBody}</p>
					</div>
				</section>

				<section className="share-feature share-feature-viewpoint" aria-labelledby="share-viewpoint-title">
					<div className="share-feature-copy">
						<span className="share-section-index">04</span>
						<h2 id="share-viewpoint-title">{copy.viewpointTitle}</h2>
						<p>{copy.viewpointBody}</p>
					</div>
					<figure>
						<img src="/images/gsplat-share-viewpoint.webp" alt={copy.viewpointAlt} />
					</figure>
				</section>

				<section className="share-controls" aria-label={copy.viewpointEyebrow}>
					{copy.controls.map((item, index) => (
						<article key={item.title}>
							<span>0{index + 1}</span>
							<h3>{item.title}</h3>
							<p>{item.body}</p>
						</article>
					))}
				</section>

				<section id="how-it-works" className="share-steps" aria-labelledby="share-steps-title">
					<div className="share-section-heading">
						<span className="share-section-index">05</span>
						<h2 id="share-steps-title">{copy.stepsTitle}</h2>
					</div>
					<ol>
						{copy.steps.map(item => (
							<li key={item.number}>
								<span>{item.number}</span>
								<div>
									<h3>{item.title}</h3>
									<p>{item.body}</p>
								</div>
							</li>
						))}
					</ol>
				</section>

				<section id="formats" className="share-formats" aria-labelledby="share-formats-title">
					<div>
						<span className="share-section-index">06</span>
						<h2 id="share-formats-title">{copy.formatsTitle}</h2>
						<p>{copy.formatsIntro}</p>
					</div>
					<dl>
						{copy.formats.map(item => (
							<div key={item.title}>
								<dt>{item.title}</dt>
								<dd>{item.body}</dd>
							</div>
						))}
					</dl>
				</section>

				<section className="share-faq" aria-labelledby="share-faq-title">
					<div>
						<span className="share-section-index">07</span>
						<h2 id="share-faq-title">{copy.faqTitle}</h2>
					</div>
					<div className="share-faq-list">
						{copy.faq.map(item => (
							<details key={item.title}>
								<summary>{item.title}</summary>
								<p>{item.body}</p>
							</details>
						))}
					</div>
				</section>

				<section className="share-final-action">
					<p>GSplat Share</p>
					<h2>{copy.ctaTitle}</h2>
					<div>
						<button type="button" onClick={onChooseFile} className="share-action share-action-primary">
							{copy.cta} <span aria-hidden="true">↓</span>
						</button>
						<Link to={localizedPath("/mypage")}>{copy.manage}</Link>
					</div>
				</section>
			</main>

			<footer className="share-landing-footer">
				<div className="share-footer-brand">
					<img className="share-brand-mark" src="/images/gsplat-share-mark.png" alt="" />
					<span>GSplat Share</span>
				</div>
				<p>{t("footer.tagline")}</p>
				<nav aria-label={t("nav.about")}>
					<Link to={localizedPath("/about")}>{t("footer.about")}</Link>
					<Link to={localizedPath("/terms")}>{t("footer.terms")}</Link>
					<Link to={localizedPath("/privacy")}>{t("footer.privacy")}</Link>
					<Link to={localizedPath("/contact")}>{t("footer.contact")}</Link>
				</nav>
			</footer>
		</div>
	);
}
